/**
/**
 * UserReview Component
 *
 * Displays the user review section for a course, allowing users to write and submit reviews.
 * Handles review state, star rating, and review submission. Renders different UI based on whether the user is writing a review.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of review form and review button
 * - Handles star rating and review text input
 * - Dispatches Redux actions for submitting and fetching reviews
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {boolean} props.showWriteReview - Whether the review form is shown
 * @param {Function} props.showWriteReviewHandler - Handler to toggle review form
 * @param {Object} props.dataCourse - Data object containing course info for review
 * @returns {JSX.Element} The rendered user review section
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getCourseReviews,
  ratingCourse
} from 'store/course-actions';
import ReactStars from 'react-rating-stars-component';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import classes from './UserReview.module.css';

const UserReview = props => {
  const dispatch = useDispatch();
  const dataCourseReview = useSelector(state => state.courseInfo);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  /**
   * Handler to show/hide the review form
   * Passed as prop to Button and invoked on click
   */
  const showWriteReviewHandler = () => {
    props.showWriteReviewHandler();
  };

  /**
   * Handler for star rating change
   * Passed as prop to ReactStars
   * @param {number} nextValue - New rating value
   */
  const onStarRating = nextValue => {
    setRating(nextValue);
  };

  /**
   * Handler for review text change
   * Passed as prop to TextField
   */
  const handleChange = event => {
    setReview(event.target.value);
  };

  /**
   * Handler to submit the review
   * Dispatches Redux action to submit review
   */
  const sendReview = () => {
    dispatch(ratingCourse(props.dataCourse.idLista, rating, review));
  };

  useEffect(() => {
    if (dataCourseReview.userReview.date) {
      props.showWriteReviewHandler();
      dispatch(getCourseReviews(props.dataCourse.idLista));
    }
  }, [dataCourseReview.userReview, dispatch]);

  return (
    <>
      {!props.showWriteReview ? (
        <>
          <p className={classes.userReviewTitle}>
            Escribir una reseña sobre este curso
          </p>
          <p className={classes.userReviewSubtitle}>
            Comparte tu opinion con la comunidad
          </p>
          <Button
            className={classes.userReviewBtn} // Prop passed: button styling
            onClick={showWriteReviewHandler} // Prop passed: click handler
          >
            Escribir mi opinión
          </Button>
        </>
      ) : (
        <>
          <Button
            className={classes.backBtn} // Prop passed: button styling
            onClick={showWriteReviewHandler} // Prop passed: click handler
          >
            <ArrowBackIcon className={classes.backIcon} />
            Regresar
          </Button>
          <aside className={classes.reviewSection}>
            <Typography className={classes.createReviewTitle}>
              Crear reseña
            </Typography>
            <figure className={classes.reviewCourseImage}>
              <img
                className={classes.img} // Prop passed: image styling
                src={props.dataCourse.imgPathBig} // Prop passed: course image URL
                alt={props.dataCourse.nombre} // Prop passed: course name for alt text
              />
            </figure>
            <p className={classes.reviewCourseTitle}>
              {props.dataCourse.nombre}
              {/* Prop passed: course name */}
            </p>
            <p className={classes.reviewCourseEmisor}>
              {props.dataCourse.nameEmisor}
              {/* Prop passed: course emitter name */}
            </p>
          </aside>
          <aside className={classes.reviewSection}>
            <Typography className={classes.createReviewTitle}>
              Calificación general
            </Typography>
            <ReactStars
              count={5}
              isHalf={true}
              size={24}
              activeColor="#e6a817"
              emptyStarColor="#d6d6d6"
              value={rating} // Prop passed: current rating value
              onChange={onStarRating} // Prop passed: rating change handler
            />
          </aside>
          <aside className={classes.reviewSection}>
            <TextField
              id="outlined-textarea"
              label="Escribe tu reseña"
              multiline
              rows="4"
              className={classes.textFieldReview} // Prop passed: text field styling
              value={review} // Prop passed: review text value
              onChange={handleChange} // Prop passed: text change handler
              margin="normal"
              variant="outlined"
            />
            <div className={classes.sendReviewBtnContainer}>
              <Button
                className={`$${
                  review && rating > 0
                    ? classes.btnSendReview
                    : classes.btnSendReviewDisabled
                }`} // Prop passed: button styling based on review/rating
                disabled={!(review && rating > 0)} // Prop passed: button disabled state
                onClick={sendReview} // Prop passed: review submit handler
              >
                Enviar
              </Button>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
UserReview.propTypes = {
  /**
   * Boolean indicating whether the user has access to the course
   * Passed as prop to control review access
   */
  accessCourse: PropTypes.bool,
  /**
   * Boolean indicating whether the review form is shown
   * Passed as prop to control review form visibility
   */
  showWriteReview: PropTypes.bool,
  /**
   * Handler to toggle review form
   * Passed as prop to Button and invoked on click
   */
  showWriteReviewHandler: PropTypes.func,
  /**
   * Data object containing course info for review
   * Passed as prop to child components for course details
   */
  dataCourse: PropTypes.object
};

UserReview.defaultProps = {
  accessCourse: false,
  showWriteReview: false,
  showWriteReviewHandler: () => {},
  dataCourse: {}
};

export default UserReview;
